import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Typography, IconButton, Button, Checkbox,
  TextField, Select, MenuItem, Tooltip, CircularProgress, Chip,
  InputAdornment, LinearProgress,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import MapIcon from '@material-ui/icons/Map';
import DeleteIcon from '@material-ui/icons/Delete';
import SendIcon from '@material-ui/icons/Send';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import DirectionsIcon from '@material-ui/icons/Directions';
import ScheduleIcon from '@material-ui/icons/Schedule';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import WarningIcon from '@material-ui/icons/Warning';
import PlaceIcon from '@material-ui/icons/Place';
import LocalHotelIcon from '@material-ui/icons/LocalHotel';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import SecurityIcon from '@material-ui/icons/Security';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import StarIcon from '@material-ui/icons/Star';
import SearchIcon from '@material-ui/icons/Search';
import SortIcon from '@material-ui/icons/Sort';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import TimelineIcon from '@material-ui/icons/Timeline';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import { setActiveView } from '../../store/slices/uiSlice';
import RoutesView from '../Routes/RoutesView';
import OrdersOperationsView from '../Orders/OrdersOperationsView';
import OperationsBoard from '../Operations/OperationsBoard';
import DashboardIcon from '@material-ui/icons/Dashboard';
import TuneIcon from '@material-ui/icons/Tune';
import HistoryIcon from '@material-ui/icons/History';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useSelector, useDispatch } from 'react-redux';
import { selectSavedRoutes, deleteRouteFromAPI, fetchSavedRoutes, dispatchRoutes, updateRouteStatus, fetchDispatchHistory } from '../../store/slices/routesSlice';
import ConfirmDialog from './ConfirmDialog';
import DispatchHistoryPanel from './DispatchHistoryPanel';
import ManageFavoritesDialog from './ManageFavoritesDialog';
import VRPView from '../VRP/VRPView';
import { selectDrivers } from '../../store/slices/driversSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import HomeIcon from '@material-ui/icons/Home';
import {
  selectLocations, selectLocationsLoading,
  fetchLocations, createLocation, updateLocation, deleteLocation,
} from '../../store/slices/locationsSlice';
import { AuthContext } from '../../App';
import DriverQuickEditDialog from './DriverQuickEditDialog';
import { t } from '../../i18n';
import { getCurrency } from '../../utils/currencies';
import { FUEL_TYPES } from '../Sidebar/VehicleDriverForm';
import { geocodeSearchMulti } from '../../utils/mapboxApi';

const LOCATION_TYPES = [
  { value: 'warehouse', icon: HomeIcon, color: '#6366f1' },
  { value: 'safe_point', icon: SecurityIcon, color: '#22c55e' },
  { value: 'overnight', icon: LocalHotelIcon, color: '#8b5cf6' },
  { value: 'poi', icon: StarIcon, color: '#f59e0b' },
  { value: 'fuel', icon: LocalGasStationIcon, color: '#ef4444' },
  { value: 'garage', icon: PlaceIcon, color: '#3b82f6' },
  { value: 'other', icon: MoreHorizIcon, color: '#64748b' },
];

function getTypeLabel(lang, type) {
  const map = {
    warehouse: 'typeWarehouse',
    safe_point: 'typeSafePoint',
    overnight: 'typeOvernight',
    poi: 'typePoi',
    fuel: 'typeFuel',
    garage: 'typeGarage',
    other: 'typeOther',
  };
  return t(lang, map[type] || 'typeOther');
}

function getTypeConfig(type) {
  return LOCATION_TYPES.find(lt => lt.value === type) || LOCATION_TYPES[4];
}

const ROUTES_PER_PAGE = 20;

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)',
    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  dialog: {
    background: '#ffffff', borderRadius: 16, width: 980, maxWidth: '97vw',
    height: '90vh', maxHeight: 780,
    boxShadow: '0 25px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.04)',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
  },
  /* ── Top header bar ── */
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', height: 56,
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    borderBottom: 'none',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: 14,
  },
  headerLogo: {
    width: 34, height: 34, borderRadius: 8,
    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 10px rgba(59,130,246,0.4)',
  },
  /* ── Layout: sidebar + content ── */
  layoutRow: {
    display: 'flex', flex: 1, overflow: 'hidden',
  },
  sidebar: {
    width: 220, flexShrink: 0,
    background: '#fff',
    borderRight: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column',
    padding: '12px 10px',
    gap: 2,
  },
  sidebarLabel: {
    fontSize: '0.5rem', fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.1em',
    padding: '8px 10px 4px',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px',
    cursor: 'pointer',
    fontSize: '0.74rem', fontWeight: 500, color: '#64748b',
    transition: 'all 0.18s',
    borderRadius: 10,
    '&:hover': { background: '#f1f5f9', color: '#1e293b' },
  },
  navItemActive: {
    background: '#eef2ff',
    color: '#3b82f6',
    fontWeight: 700,
    boxShadow: '0 1px 3px rgba(59,130,246,0.08)',
    '&:hover': { background: '#dbeafe' },
  },
  navBadge: {
    marginLeft: 'auto',
    fontSize: '0.54rem', fontWeight: 700,
    background: '#f1f5f9', color: '#64748b',
    borderRadius: 8, padding: '2px 8px',
    lineHeight: 1.5, minWidth: 18, textAlign: 'center',
  },
  navBadgeActive: {
    background: '#3b82f6', color: '#ffffff',
  },
  /* ── Content area ── */
  content: {
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    minWidth: 0,
  },
  contentHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '16px 24px 14px',
    borderBottom: '1px solid #f1f5f9',
    flexShrink: 0,
    background: '#fff',
  },
  contentTitle: {
    fontSize: '0.95rem', fontWeight: 700, color: '#0f172a',
    flex: 1, letterSpacing: '-0.01em',
  },
  /* ── Search / toolbar area (fixed, not scrollable) ── */
  searchBar: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px',
    borderBottom: '1px solid #f1f5f9', background: '#fafbfc', flexWrap: 'wrap',
    flexShrink: 0,
  },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 24px',
    borderBottom: '1px solid #f1f5f9', background: '#fff',
    flexShrink: 0,
  },
  /* ── Scrollable body ── */
  body: { flex: 1, overflow: 'auto', padding: 0 },
  /* ── Route cards ── */
  routeCard: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
    borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
    transition: 'all 0.18s ease',
    position: 'relative',
    '&:hover': { background: '#f0f4ff' },
  },
  routeCardSelected: {
    background: '#f0f4ff',
    borderLeft: '3px solid #3b82f6',
    paddingLeft: 17,
    boxShadow: 'inset 0 0 0 1px rgba(59,130,246,0.08)',
    '&:hover': { background: '#e0eaff' },
  },
  routeInfo: { flex: 1, minWidth: 0 },
  routeName: { fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' },
  routePoints: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: '0.68rem', color: '#64748b', marginTop: 3,
  },
  routeMeta: { display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' },
  metaChip: { height: 20, fontSize: '0.58rem', fontWeight: 600, borderRadius: 6 },
  /* ── Locations ── */
  locationCard: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
    borderBottom: '1px solid #f1f5f9',
    transition: 'all 0.15s',
    '&:hover': { background: '#f8fafc', transform: 'translateX(2px)' },
  },
  locationIcon: {
    width: 36, height: 36, borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  },
  locationInfo: { flex: 1, minWidth: 0 },
  locationName: { fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' },
  locationMeta: { fontSize: '0.65rem', color: '#94a3b8', marginTop: 2 },
  /* ── Common ── */
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '56px 24px', gap: 12, textAlign: 'center',
  },
  addForm: {
    padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#fafbfc',
    '& .MuiAutocomplete-root': {
      '& .MuiInputBase-root': { fontSize: '0.78rem', padding: '4px 8px !important' },
      '& .MuiOutlinedInput-input': { padding: '6px 8px !important' },
      '& .MuiAutocomplete-endAdornment': { right: 4, top: 'calc(50% - 12px)' },
    },
    '& .MuiAutocomplete-listbox': { maxHeight: 200 },
  },
  formRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' },
  actionBtn: {
    fontSize: '0.72rem', textTransform: 'none', fontWeight: 600, borderRadius: 8,
    transition: 'all 0.15s',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  },
  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, padding: '12px 24px',
    borderTop: '1px solid #f1f5f9', background: '#fafbfc',
  },
  /* ── Detail panel (inside content) ── */
  detailPanel: {
    padding: '20px 24px', fontSize: '0.72rem',
  },
  detailSection: { marginBottom: 18 },
  detailSectionTitle: {
    fontSize: '0.62rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: 8,
    paddingBottom: 6, borderBottom: '2px solid #eef2ff',
  },
  detailRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0',
    fontSize: '0.72rem', color: '#334155',
  },
  detailLabel: {
    fontSize: '0.66rem', color: '#94a3b8', minWidth: 95, flexShrink: 0,
  },
  detailValue: {
    fontSize: '0.72rem', color: '#1e293b', fontWeight: 600,
  },
  waypointItem: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
    background: '#f8fafc', borderRadius: 8, marginBottom: 4,
    border: '1px solid #e2e8f0', fontSize: '0.7rem',
    transition: 'all 0.12s',
    '&:hover': { background: '#eef2ff', borderColor: '#c7d2fe' },
  },
  costGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
    gap: 10, marginTop: 8,
  },
  costCard: {
    padding: '12px 10px', borderRadius: 10, textAlign: 'center',
    border: '1px solid #e2e8f0', background: '#fafbfc',
    transition: 'all 0.15s',
    '&:hover': { background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderColor: '#c7d2fe' },
  },
  costValue: { fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' },
  costLabel: { fontSize: '0.56rem', color: '#94a3b8', textTransform: 'uppercase', marginTop: 3, letterSpacing: '0.03em' },
  roadBar: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
  },
  roadLabel: { fontSize: '0.62rem', color: '#64748b', minWidth: 75, fontWeight: 500 },
  roadProgress: { flex: 1, height: 7, borderRadius: 4 },
  roadPct: { fontSize: '0.6rem', color: '#64748b', minWidth: 32, textAlign: 'right', fontWeight: 600 },
  /* ── Route detail modal (popup over the manage dialog) ── */
  detailOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)',
    zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  detailDialog: {
    background: '#ffffff', borderRadius: 16, width: 600, maxWidth: '92vw',
    maxHeight: '82vh', boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
  },
  detailDialogHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px',
    background: '#0f172a',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  detailDialogBody: {
    flex: 1, overflow: 'auto',
  },
  /* ── Confirm delete dialog ── */
  confirmOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
    zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  confirmDialog: {
    background: '#ffffff', borderRadius: 14, width: 420, maxWidth: '90vw',
    boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  confirmHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '20px 24px 12px',
  },
  confirmIconWrap: {
    width: 44, height: 44, borderRadius: 11,
    background: 'rgba(239,68,68,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  confirmBody: { padding: '0 24px 8px' },
  confirmActions: {
    display: 'flex', gap: 10, padding: '12px 24px 20px',
    justifyContent: 'flex-end',
  },
  confirmCancelBtn: {
    fontSize: '0.78rem', textTransform: 'none', fontWeight: 600,
    borderRadius: 10, padding: '7px 20px', color: '#64748b',
    border: '1px solid #e2e8f0',
    '&:hover': { background: '#f8fafc' },
  },
  confirmDeleteBtn: {
    fontSize: '0.78rem', textTransform: 'none', fontWeight: 600,
    borderRadius: 10, padding: '7px 20px', color: '#fff',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
    '&:hover': { background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)' },
  },
}));

function ManageDialog({ open, onClose, lang, onShowRoutesOnMap, initialSection, fullScreen }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, modules, clientExternalId, clientSettings } = useContext(AuthContext);
  const savedRoutes = useSelector(selectSavedRoutes);
  const drivers = useSelector(selectDrivers);
  // Edição rápida do motorista a partir dos cartões de rota (Estado das Rotas).
  const [editDriver, setEditDriver] = useState(null);
  const driverIdOf = (route) => route?.driverId || route?.routeDrivers?.[0]?.driverId || null;
  const openEditDriver = (route) => {
    const did = driverIdOf(route);
    if (!did) return;
    const full = drivers.find(d => String(d.id) === String(did));
    setEditDriver(full || { id: did, name: route.driverName || route.routeDrivers?.[0]?.driver?.name });
  };
  const vehicles = useSelector(selectVehicles);
  const locations = useSelector(selectLocations);
  const locationsLoading = useSelector(selectLocationsLoading);

  const hasModule = (mod) => !mod || !modules || modules.length === 0 || modules.includes(mod);

  // Landing: Painel de Operações quando o módulo ops_board está ativo (mercadoria);
  // senão, Estado das Rotas como antes.
  const [activeSection, setActiveSectionRaw] = useState(hasModule('ops_board') ? 'ops-board' : 'status');
  // Legacy: old code sometimes sets 'routes'/'dispatch' which aren't in the current nav.
  // Collapse those into 'status' (the only always-visible item) to avoid a blank right panel.
  const setActiveSection = (s) => {
    if (s === 'routes' || s === 'dispatch') return setActiveSectionRaw('status');
    setActiveSectionRaw(s);
  };

  // OT events stream (loaded when statusFilter === 'history')
  const [otEvents, setOtEvents] = useState([]);
  const [otEventsLoading, setOtEventsLoading] = useState(false);
  React.useEffect(() => {
    if (activeSection !== 'status' || !token) return;
    let cancelled = false;
    setOtEventsLoading(true);
    fetch('/planner-api/transport-orders/events/all?limit=300', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => { if (!cancelled) setOtEvents(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setOtEvents([]); })
      .finally(() => { if (!cancelled) setOtEventsLoading(false); });
    return () => { cancelled = true; };
  }, [activeSection, token]);

  // Listen for external section change (e.g. from OT "Ir para Operação")
  React.useEffect(() => {
    function onOpenSection(e) {
      // Support both string payloads ('status') and object payloads
      // ({ section, statusFilter }). The latter lets callers also pre-set the
      // status-tab filter (e.g. opening directly on 'kanban').
      if (!e.detail) return;
      if (typeof e.detail === 'string') {
        setActiveSection(e.detail);
      } else {
        if (e.detail.section) setActiveSection(e.detail.section);
        if (e.detail.statusFilter) setStatusFilter(e.detail.statusFilter);
      }
    }
    window.addEventListener('manageOpenSection', onOpenSection);
    return () => window.removeEventListener('manageOpenSection', onOpenSection);
  }, []);

  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [detailRoute, setDetailRoute] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: '', type: 'poi', latitude: '', longitude: '', address: '', notes: '' });
  const [editingLoc, setEditingLoc] = useState(null); // { id, name }

  // Dispatch state: { driverId: [routeId, ...] }
  const [dispatchMap, setDispatchMap] = useState({});
  const [routeOTs, setRouteOTs] = useState({}); // { routeId: [ots] }
  const [dragOverDriver, setDragOverDriver] = useState(null);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchRouteFilter, setDispatchRouteFilter] = useState('');
  const [dispatchDriverFilter, setDispatchDriverFilter] = useState('');
  const [routeDeleteConfirm, setRouteDeleteConfirm] = useState(null);
  const [driverPage, setDriverPage] = useState(1);
  const DRIVERS_PER_PAGE = 10;
  const [updatingStatus, setUpdatingStatus] = useState(null); // routeId being updated
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'all'

  // Dispatch history state
  const [expandedHistory, setExpandedHistory] = useState(null); // routeId or null
  const [historyData, setHistoryData] = useState({}); // { [routeId]: [...entries] }
  const [historyLoading, setHistoryLoading] = useState(false);

  // Re-dispatch state
  const [reDispatchRoute, setReDispatchRoute] = useState(null); // routeId or null
  const [reDispatchDriver, setReDispatchDriver] = useState('');
  const [reDispatchNotes, setReDispatchNotes] = useState('');

  // Search, filter, sort, pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [currentPage, setCurrentPage] = useState(1);

  // Location address search state
  const [addressInput, setAddressInput] = useState('');
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showManualCoords, setShowManualCoords] = useState(false);

  // Debounced address search
  useEffect(() => {
    if (addressInput.length < 2) { setAddressOptions([]); return; }
    setAddressLoading(true);
    const timer = setTimeout(async () => {
      const results = await geocodeSearchMulti(addressInput);
      setAddressOptions(results);
      setAddressLoading(false);
    }, 350);
    return () => { clearTimeout(timer); setAddressLoading(false); };
  }, [addressInput]);

  function handleAddressSelect(_, selectedOption) {
    if (!selectedOption) return;
    setNewLocation(prev => ({
      ...prev,
      address: selectedOption.name,
      latitude: selectedOption.coordinates[1].toFixed(7),
      longitude: selectedOption.coordinates[0].toFixed(7),
    }));
  }

  useEffect(() => {
    if (open && token) {
      dispatch(fetchSavedRoutes(token));
      dispatch(fetchLocations(token));
      // Fetch OTs grouped by route
      fetch('/planner-api/transport-orders?page=1&limit=200', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(data => {
          const ots = data.data || data;
          const grouped = {};
          ots.forEach(ot => {
            if (ot.assignedRouteId) {
              if (!grouped[ot.assignedRouteId]) grouped[ot.assignedRouteId] = [];
              grouped[ot.assignedRouteId].push(ot);
            }
          });
          setRouteOTs(grouped);
        })
        .catch(() => {});
    }
  }, [open, token, dispatch]);

  // Reset filters when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setFilterVehicle('');
      setFilterDriver('');
      setCurrentPage(1);
      setSelectedRoutes([]);
      setDetailRoute(null);
      setShowDeleteConfirm(false);
      setActiveSection('status');
    }
  }, [open]);

  // Extract unique vehicles and drivers for filter dropdowns
  const uniqueVehicles = useMemo(() => {
    const set = new Set();
    savedRoutes.forEach(r => { if (r.vehiclePlate) set.add(r.vehiclePlate); });
    return Array.from(set).sort();
  }, [savedRoutes]);

  const uniqueDrivers = useMemo(() => {
    const set = new Set();
    savedRoutes.forEach(r => { if (r.driverName) set.add(r.driverName); });
    return Array.from(set).sort();
  }, [savedRoutes]);

  // Filter, sort, paginate routes
  const filteredRoutes = useMemo(() => {
    let routes = [...savedRoutes];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      routes = routes.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.origin?.name || '').toLowerCase().includes(q) ||
        (r.destination?.name || '').toLowerCase().includes(q) ||
        (r.vehiclePlate || '').toLowerCase().includes(q) ||
        (r.driverName || '').toLowerCase().includes(q) ||
        (r.cargoRef || '').toLowerCase().includes(q)
      );
    }
    if (filterVehicle) {
      routes = routes.filter(r => r.vehiclePlate === filterVehicle);
    }
    if (filterDriver) {
      routes = routes.filter(r => r.driverName === filterDriver);
    }
    if (sortBy === 'date') {
      routes.sort((a, b) => new Date(b.savedAt || 0) - new Date(a.savedAt || 0));
    } else if (sortBy === 'name') {
      routes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'distance') {
      routes.sort((a, b) => (b.computedRoute?.distance || 0) - (a.computedRoute?.distance || 0));
    }
    return routes;
  }, [savedRoutes, searchQuery, filterVehicle, filterDriver, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredRoutes.length / ROUTES_PER_PAGE));
  const paginatedRoutes = filteredRoutes.slice((currentPage - 1) * ROUTES_PER_PAGE, currentPage * ROUTES_PER_PAGE);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterVehicle, filterDriver, sortBy]);

  // Não usamos "return null" — mantemos a DOM pré-construída para abrir instantaneamente

  // ──── Routes Tab ────
  function toggleRouteSelect(routeId) {
    setSelectedRoutes(prev => {
      if (prev.includes(routeId)) return prev.filter(id => id !== routeId);
      if (prev.length >= 5) return prev;
      return [...prev, routeId];
    });
  }

  function handleShowOnMap() {
    const routes = savedRoutes.filter(r => selectedRoutes.includes(r.id));
    if (onShowRoutesOnMap) onShowRoutesOnMap(routes);
  }

  function handleDeleteSelected() {
    setShowDeleteConfirm(true);
  }

  function confirmDelete() {
    selectedRoutes.forEach(id => {
      const route = savedRoutes.find(r => r.id === id);
      if (route?.dbId && token) {
        dispatch(deleteRouteFromAPI({ token, routeId: route.dbId }));
      }
    });
    setSelectedRoutes([]);
    setShowDeleteConfirm(false);
  }

  // Dispatch handlers
  // Drag driver/vehicle → drop on route
  function handleDragStartDriver(e, driverId) {
    e.dataTransfer.setData('text/plain', String(driverId));
    e.dataTransfer.effectAllowed = 'move';
  }
  const [dragOverRoute, setDragOverRoute] = useState(null);
  function handleDropOnRoute(e, routeId) {
    e.preventDefault();
    setDragOverRoute(null);
    const driverId = e.dataTransfer.getData('text/plain');
    if (!driverId) return;
    setDispatchMap(prev => {
      const existing = prev[driverId] || [];
      if (existing.includes(routeId)) return prev;
      return { ...prev, [driverId]: [...existing, routeId] };
    });
  }
  // Keep old functions for compatibility
  function handleDragStart(e, routeId) {
    e.dataTransfer.setData('text/plain', routeId);
    e.dataTransfer.effectAllowed = 'move';
  }
  function handleDropOnDriver(e, driverId) {
    e.preventDefault();
    setDragOverDriver(null);
    const routeId = e.dataTransfer.getData('text/plain');
    if (!routeId) return;
    setDispatchMap(prev => {
      const existing = prev[driverId] || [];
      if (existing.includes(routeId)) return prev;
      return { ...prev, [driverId]: [...existing, routeId] };
    });
  }
  function handleRemoveFromDispatch(driverId, routeId) {
    setDispatchMap(prev => {
      const updated = { ...prev };
      updated[driverId] = (updated[driverId] || []).filter(r => r !== routeId);
      if (updated[driverId].length === 0) delete updated[driverId];
      return updated;
    });
  }
  async function handleConfirmDispatch() {
    const assignments = [];
    for (const [driverId, routeIds] of Object.entries(dispatchMap)) {
      const driver = drivers.find(d => String(d.id) === String(driverId));
      for (const routeId of routeIds) {
        const route = savedRoutes.find(r => r.id === routeId);
        if (route?.dbId) {
          const entry = { routeId: route.dbId, driverId: Number(driverId) };
          // Include driverData for integrated mode (driver has externalId)
          if (driver?.externalId) {
            entry.driverData = {
              externalId: driver.externalId,
              name: driver.name,
              email: driver.email || null,
              licenseClass: driver.licenseClass || null,
              tachoCard: driver.tachoCard || null,
              phone: driver.phone || null,
            };
          }
          assignments.push(entry);
        }
      }
    }
    if (assignments.length === 0) return;
    setDispatching(true);
    try {
      await dispatch(dispatchRoutes({ token, assignments })).unwrap();
      setDispatchMap({});
      // Refresh saved routes to get updated routeDrivers
      dispatch(fetchSavedRoutes(token));
    } catch (err) {
      alert(err.message);
    }
    setDispatching(false);
  }

  // ──── Fleet optimization (load-balance routes across drivers) ────
  function haversineKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function handleOptimizeDispatch() {
    const undispatched = savedRoutes.filter(r => !r.sentToVehicle);
    if (!undispatched.length || !drivers.length) return;
    // Sort longest routes first (greedy bin-packing heuristic)
    const sorted = [...undispatched].sort((a, b) =>
      (b.computedRoute?.distance || 0) - (a.computedRoute?.distance || 0)
    );
    const workload = {};
    drivers.forEach(d => { workload[d.id] = 0; });
    const newMap = {};
    sorted.forEach(route => {
      let targetDriver = drivers[0];
      // If vehicles have GPS positions, weight by proximity to route origin
      const vehiclesWithPos = vehicles.filter(v => v.position?.latitude);
      if (vehiclesWithPos.length > 0 && route.origin?.coordinates) {
        const [lng, lat] = route.origin.coordinates;
        let minScore = Infinity;
        drivers.forEach(d => {
          // Proximity bonus: find closest vehicle to this route's origin
          let minDist = 999;
          vehiclesWithPos.forEach(v => {
            const dist = haversineKm(lat, lng, v.position.latitude, v.position.longitude);
            if (dist < minDist) minDist = dist;
          });
          const score = workload[d.id] + minDist * 1000; // weight proximity
          if (score < minScore) { minScore = score; targetDriver = d; }
        });
      } else {
        // Pure load-balancing
        targetDriver = drivers.reduce((min, d) => workload[d.id] < workload[min.id] ? d : min, drivers[0]);
      }
      if (!newMap[targetDriver.id]) newMap[targetDriver.id] = [];
      newMap[targetDriver.id].push(route.id);
      workload[targetDriver.id] += route.computedRoute?.distance || 0;
    });
    setDispatchMap(newMap);
  }

  // ──── Status update (Planeado vs Real) ────
  async function handleUpdateStatus(routeDbId, newStatus) {
    if (!token) return;
    setUpdatingStatus(routeDbId);
    try {
      await dispatch(updateRouteStatus({ token, routeId: routeDbId, status: newStatus })).unwrap();
    } catch (err) {
      alert(err.message);
    }
    setUpdatingStatus(null);
  }

  // ──── Dispatch History ────
  async function handleToggleHistory(routeDbId) {
    if (expandedHistory === routeDbId) {
      setExpandedHistory(null);
      return;
    }
    setExpandedHistory(routeDbId);
    if (!historyData[routeDbId]) {
      setHistoryLoading(true);
      try {
        const result = await dispatch(fetchDispatchHistory({ token, routeId: routeDbId })).unwrap();
        setHistoryData(prev => ({ ...prev, [routeDbId]: result }));
      } catch (err) {
        console.error('fetchDispatchHistory error:', err);
        setHistoryData(prev => ({ ...prev, [routeDbId]: [] }));
      }
      setHistoryLoading(false);
    }
  }

  // ──── Re-dispatch ────
  async function handleConfirmReDispatch(route) {
    if (!reDispatchDriver || !route?.dbId) return;
    const driver = drivers.find(d => String(d.id) === String(reDispatchDriver));
    if (!driver) return;
    const entry = { routeId: route.dbId, driverId: Number(reDispatchDriver) };
    if (driver.externalId) {
      entry.driverData = {
        externalId: driver.externalId,
        name: driver.name,
        email: driver.email || null,
        licenseClass: driver.licenseClass || null,
        tachoCard: driver.tachoCard || null,
        phone: driver.phone || null,
      };
    }
    if (reDispatchNotes) entry.notes = reDispatchNotes;
    setDispatching(true);
    try {
      await dispatch(dispatchRoutes({ token, assignments: [entry] })).unwrap();
      setReDispatchRoute(null);
      setReDispatchDriver('');
      setReDispatchNotes('');
      // Refresh routes and clear cached history
      dispatch(fetchSavedRoutes(token));
      setHistoryData(prev => { const copy = { ...prev }; delete copy[route.dbId]; return copy; });
    } catch (err) {
      alert(err.message);
    }
    setDispatching(false);
  }

  function getStatusConfig(dbStatus) {
    const map = {
      draft:       { label: 'Rascunho',    bg: '#f1f5f9', color: '#64748b' },
      planned:     { label: 'Atribuída',    bg: '#dbeafe', color: '#1d4ed8' },
      in_progress: { label: 'Em Trânsito', bg: '#d1fae5', color: '#059669' },
      completed:   { label: 'Concluída',   bg: '#d1fae5', color: '#047857' },
      cancelled:   { label: 'Cancelada',   bg: '#fee2e2', color: '#dc2626' },
    };
    return map[dbStatus] || map.draft;
  }

  function formatTimeDiff(isoA, isoB) {
    if (!isoA || !isoB) return null;
    const diffMin = Math.round((new Date(isoB) - new Date(isoA)) / 60000);
    if (diffMin === 0) return 'a tempo';
    const abs = Math.abs(diffMin);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    const label = h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ''}` : `${m}min`;
    return diffMin > 0 ? `+${label} atraso` : `${label} adiantado`;
  }

  // ──── Locations Tab ────
  async function handleAddLocation() {
    if (!newLocation.name || !newLocation.latitude || !newLocation.longitude) return;
    if (!token) { console.error('No token for createLocation'); return; }
    try {
      const result = await dispatch(createLocation({
        token,
        location: {
          name: newLocation.name,
          type: newLocation.type,
          latitude: parseFloat(newLocation.latitude),
          longitude: parseFloat(newLocation.longitude),
          address: newLocation.address || null,
          notes: newLocation.notes || null,
        },
      }));
      if (result.error) {
        console.error('createLocation error:', result.error);
        alert(t(lang, 'locationSaveError') || 'Erro ao guardar local');
        return;
      }
      // Success - reset form
      setNewLocation({ name: '', type: 'poi', latitude: '', longitude: '', address: '', notes: '' });
      setAddressInput('');
      setAddressOptions([]);
      setShowManualCoords(false);
      setShowAddForm(false);
    } catch (err) {
      console.error('createLocation exception:', err);
      alert(t(lang, 'locationSaveError') || 'Erro ao guardar local');
    }
  }

  function handleDeleteLocation(locId) {
    if (!token) return;
    dispatch(deleteLocation({ token, id: locId }));
  }

  async function handleSaveLocationEdit() {
    if (!editingLoc || !token || !editingLoc.name.trim()) return;
    await dispatch(updateLocation({ token, id: editingLoc.id, data: { name: editingLoc.name.trim() } }));
    setEditingLoc(null);
  }

  function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  function formatDateTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  // ──── Route Detail Helpers ────
  function getRouteCosts(route) {
    const cr = route.computedRoute;
    if (!cr) return null;
    const distKm = cr.distance / 1000;
    const fuelType = route.fuelType || 'diesel';
    const fuelDef = FUEL_TYPES.find(f => f.value === fuelType) || FUEL_TYPES[0];
    const currency = getCurrency(route.routeOptions?.currency);
    const consumption = route.fuelConsumption ?? fuelDef.consumptionDefault;
    const pricePerUnit = route.fuelPrice ?? fuelDef.priceDefault;
    const pricePerUnitEUR = route.fuelPrice != null ? pricePerUnit / currency.rate : pricePerUnit;
    const totalFuel = (distKm / 100) * consumption;
    const fuelCostEUR = totalFuel * pricePerUnitEUR;
    const tollCost = cr.tollCost || 0;
    const fuelCost = fuelCostEUR * currency.rate;
    const totalCost = fuelCost + (tollCost * currency.rate);
    return { fuelCost, tollCost: tollCost * currency.rate, totalCost, totalFuel, symbol: currency.symbol };
  }

  function getTachographSummary(route) {
    if (!route.stops?.length) return null;
    const breaks = route.stops.filter(s => s.type === 'break');
    const rests = route.stops.filter(s => s.type !== 'break');
    const totalBreakMin = breaks.reduce((sum, s) => sum + (s.duration || 0), 0);
    const totalRestMin = rests.reduce((sum, s) => sum + (s.duration || 0), 0);
    const drivingMin = route.computedRoute ? Math.round(route.computedRoute.duration / 60) : 0;
    return { breaks: breaks.length, rests: rests.length, totalBreakMin, totalRestMin, drivingMin };
  }

  function renderRouteDetail(route) {
    const distKm = route.computedRoute?.distance ? (route.computedRoute.distance / 1000).toFixed(1) : null;
    const durationH = route.computedRoute ? Math.floor(route.computedRoute.duration / 3600) : null;
    const durationM = route.computedRoute ? Math.floor((route.computedRoute.duration % 3600) / 60) : null;
    const costs = getRouteCosts(route);
    const tacho = getTachographSummary(route);
    const rb = route.roadBreakdown;

    return (
      <Box className={classes.detailPanel}>
        {/* Route overview */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailSectionTitle}>{t(lang, 'routeDetails')}</Typography>
          <Box className={classes.detailRow}>
            <RoomIcon style={{ fontSize: 12, color: '#22c55e' }} />
            <span className={classes.detailLabel}>{t(lang, 'origin')}:</span>
            <span className={classes.detailValue}>{route.origin?.name || '—'}</span>
          </Box>
          <Box className={classes.detailRow}>
            <FlagIcon style={{ fontSize: 12, color: '#ef4444' }} />
            <span className={classes.detailLabel}>{t(lang, 'destination')}:</span>
            <span className={classes.detailValue}>{route.destination?.name || '—'}</span>
          </Box>
          {distKm && (
            <Box className={classes.detailRow}>
              <DirectionsIcon style={{ fontSize: 12, color: '#3b82f6' }} />
              <span className={classes.detailLabel}>{t(lang, 'distance')}:</span>
              <span className={classes.detailValue}>{distKm} km</span>
            </Box>
          )}
          {durationH != null && (
            <Box className={classes.detailRow}>
              <ScheduleIcon style={{ fontSize: 12, color: '#f59e0b' }} />
              <span className={classes.detailLabel}>{t(lang, 'duration')}:</span>
              <span className={classes.detailValue}>{durationH}h {durationM}min</span>
            </Box>
          )}
        </Box>

        {/* Estimated Costs */}
        {costs && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'estimatedCosts')}</Typography>
            <Box className={classes.costGrid}>
              <Box className={classes.costCard}>
                <Typography className={classes.costValue}>{costs.fuelCost.toFixed(2)}{costs.symbol}</Typography>
                <Typography className={classes.costLabel}>{t(lang, 'fuelCostLabel')}</Typography>
              </Box>
              <Box className={classes.costCard}>
                <Typography className={classes.costValue}>{costs.tollCost.toFixed(2)}{costs.symbol}</Typography>
                <Typography className={classes.costLabel}>{t(lang, 'tollCostLabel')}</Typography>
              </Box>
              <Box className={classes.costCard} style={{ background: 'rgba(59,130,246,0.04)', borderColor: '#3b82f6' }}>
                <Typography className={classes.costValue} style={{ color: '#3b82f6' }}>{costs.totalCost.toFixed(2)}{costs.symbol}</Typography>
                <Typography className={classes.costLabel}>{t(lang, 'totalCostLabel')}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Road Breakdown */}
        {rb && Object.keys(rb).length > 0 && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'roadBreakdownLabel')}</Typography>
            {Object.entries(rb).map(([type, pct]) => (
              <Box key={type} className={classes.roadBar}>
                <span className={classes.roadLabel}>{type}</span>
                <LinearProgress variant="determinate" value={pct} className={classes.roadProgress}
                  style={{ backgroundColor: '#e2e8f0' }} />
                <span className={classes.roadPct}>{pct}%</span>
              </Box>
            ))}
          </Box>
        )}

        {/* Tachograph Summary */}
        {tacho && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'tachographSummary')}</Typography>
            <Box className={classes.detailRow}>
              <span className={classes.detailLabel}>{t(lang, 'totalDrivingTime')}:</span>
              <span className={classes.detailValue}>{Math.floor(tacho.drivingMin / 60)}h {tacho.drivingMin % 60}min</span>
            </Box>
            <Box className={classes.detailRow}>
              <span className={classes.detailLabel}>{t(lang, 'breakCount')}:</span>
              <span className={classes.detailValue}>{tacho.breaks} ({tacho.totalBreakMin}min)</span>
            </Box>
            <Box className={classes.detailRow}>
              <span className={classes.detailLabel}>{t(lang, 'restCount')}:</span>
              <span className={classes.detailValue}>{tacho.rests} ({tacho.totalRestMin}min)</span>
            </Box>
          </Box>
        )}

        {/* Vehicle / Driver / Dates */}
        <Box className={classes.detailSection}>
          {route.vehiclePlate && (
            <Box className={classes.detailRow}>
              <LocalShippingIcon style={{ fontSize: 12, color: '#22c55e' }} />
              <span className={classes.detailLabel}>{t(lang, 'vehicle')}:</span>
              <span className={classes.detailValue}>{route.vehiclePlate} {route.vehicleType ? `(${route.vehicleType})` : ''}</span>
            </Box>
          )}
          {route.driverName && (
            <Box className={classes.detailRow}>
              <PersonIcon style={{ fontSize: 12, color: '#8b5cf6' }} />
              <span className={classes.detailLabel}>{t(lang, 'driver')}:</span>
              <span className={classes.detailValue}>{route.driverName}</span>
            </Box>
          )}
          {route.departureTime && (
            <Box className={classes.detailRow}>
              <ScheduleIcon style={{ fontSize: 12, color: '#06b6d4' }} />
              <span className={classes.detailLabel}>{t(lang, 'departure')}:</span>
              <span className={classes.detailValue}>{formatDateTime(route.departureTime)}</span>
            </Box>
          )}
          {route.arrivalTimeWindow && (
            <Box className={classes.detailRow}>
              <ScheduleIcon style={{ fontSize: 12, color: '#ec4899' }} />
              <span className={classes.detailLabel}>{t(lang, 'arrivalWindow')}:</span>
              <span className={classes.detailValue}>
                {route.arrivalTimeWindow.min && formatDateTime(route.arrivalTimeWindow.min)}
                {route.arrivalTimeWindow.max && ` — ${formatDateTime(route.arrivalTimeWindow.max)}`}
              </span>
            </Box>
          )}
          {route.cargoRef && (
            <Box className={classes.detailRow}>
              <span className={classes.detailLabel}>{t(lang, 'cargoRef')}:</span>
              <span className={classes.detailValue}>{route.cargoRef}</span>
            </Box>
          )}
          <Box className={classes.detailRow}>
            <span className={classes.detailLabel}>{t(lang, 'sentStatus')}:</span>
            <span className={classes.detailValue} style={{ color: route.sentToVehicle ? '#22c55e' : '#94a3b8' }}>
              {route.sentToVehicle ? t(lang, 'sentYes') : t(lang, 'sentNo')}
              {route.sentToVehicleAt && ` (${formatDateTime(route.sentToVehicleAt)})`}
            </span>
          </Box>
        </Box>

        {/* Waypoints */}
        {route.waypoints && route.waypoints.length > 0 && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'routeWaypoints')} ({route.waypoints.length})</Typography>
            {route.waypoints.map((wp, i) => (
              <Box key={i} className={classes.waypointItem}>
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#f59e0b', minWidth: 14 }}>{i + 1}</span>
                <span>{wp.name || `${wp.coordinates?.[1]?.toFixed(4)}, ${wp.coordinates?.[0]?.toFixed(4)}`}</span>
              </Box>
            ))}
          </Box>
        )}

        {/* Stops */}
        {route.stops && route.stops.length > 0 && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'routeStopsInfo')} ({route.stops.length})</Typography>
            {route.stops.map((stop, i) => (
              <Box key={i} className={classes.waypointItem}>
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#3b82f6', minWidth: 14 }}>{i + 1}</span>
                <span>{stop.type === 'break' ? '☕' : '🛏️'} Km {stop.km} — {stop.duration}min</span>
                {stop.arrivalTime && <span style={{ marginLeft: 'auto', fontSize: '0.6rem', color: '#94a3b8' }}>{stop.arrivalTime}</span>}
              </Box>
            ))}
          </Box>
        )}

        {/* Markers */}
        {route.routeMarkers && route.routeMarkers.length > 0 && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'routeMarkersList')} ({route.routeMarkers.length})</Typography>
            {route.routeMarkers.map((m, i) => {
              const typeLabels = { safe: '🛡️', overnight: '🏨', poi: '📍', fuel: '⛽' };
              return (
                <Box key={i} className={classes.waypointItem}>
                  <span>{typeLabels[m.type] || '📍'}</span>
                  <span>{m.name || '—'}</span>
                  {m.coordinates && <span style={{ marginLeft: 'auto', fontSize: '0.58rem', color: '#94a3b8' }}>{m.coordinates[1]?.toFixed(4)}, {m.coordinates[0]?.toFixed(4)}</span>}
                </Box>
              );
            })}
          </Box>
        )}

        {/* POIs */}
        {route.pois && route.pois.length > 0 && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'routePoisList')} ({route.pois.length})</Typography>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {route.pois.slice(0, 15).map((poi, i) => (
                <Chip key={i} label={poi.name || poi.type} size="small" className={classes.metaChip}
                  style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b' }} />
              ))}
              {route.pois.length > 15 && (
                <Chip label={`+${route.pois.length - 15}`} size="small" className={classes.metaChip}
                  style={{ background: 'rgba(148,163,184,0.1)', color: '#94a3b8' }} />
              )}
            </Box>
          </Box>
        )}

        {/* Route options summary */}
        {route.routeOptions && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>{t(lang, 'routeOptionsInfo')}</Typography>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {route.routeOptions.avoidTolls && <Chip label={t(lang, 'avoidTolls')} size="small" className={classes.metaChip} style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444' }} />}
              {route.routeOptions.avoidFerries && <Chip label={t(lang, 'avoidFerries')} size="small" className={classes.metaChip} style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6' }} />}
              {route.routeOptions.avoidUrban && <Chip label={t(lang, 'avoidUrban')} size="small" className={classes.metaChip} style={{ background: 'rgba(168,85,247,0.08)', color: '#a855f7' }} />}
              {route.roundTrip && <Chip label={t(lang, 'roundTrip')} size="small" className={classes.metaChip} style={{ background: 'rgba(34,197,94,0.08)', color: '#22c55e' }} />}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // ──── Sidebar navigation items (filtered by active modules) ────
  const undispatchedCount = savedRoutes.length;
  const inProgressCount = savedRoutes.filter(r => r.dbStatus === 'planned' || r.dbStatus === 'in_progress').length;
  const allNavItems = [
    // 'dispatch' and 'vrp' temporarily hidden — focus is on route execution status while we
    // build out the dispatch and VRP optimization workflows. Re-enable when ready.
    // { id: 'dispatch', icon: SendIcon, labelKey: 'dispatch', count: savedRoutes.length, module: 'route_planner' },
    // { id: 'vrp', icon: TuneIcon, label: lang === 'pt' ? 'Optimização' : 'Optimization', count: 0, module: null },
    // Live operations dashboard — HIDDEN. The same monitoring is now done
    // directly inside the enriched "Rotas" panel (per-row progress bar,
    // elapsed/remaining time, GPS indicator, click-through to the live map).
    // Kept the entry commented for quick re-enable while the new flow is
    // validated in production.
    // { id: 'operations-live', icon: LocalShippingIcon, label: lang === 'pt' ? 'Em curso' : 'In progress', count: 0, module: 'route_planner' },
    { id: 'ops-board', icon: DashboardIcon, label: lang === 'pt' ? 'Painel' : 'Board', count: 0, module: 'ops_board' },
    { id: 'status', icon: TimelineIcon, labelKey: 'routeStatus', count: inProgressCount, module: 'route_planner' },
    // Embedded view of the full RoutesView — renders inside the right pane of Operações
    { id: 'routes-library', icon: LibraryBooksIcon, label: lang === 'pt' ? 'Rotas' : 'Routes', count: savedRoutes.length, module: 'route_planner' },
    // Alarmes de velocidade — MOVIDO para o menu Frota (FleetView, tab).
  ];
  const navItems = allNavItems.filter(item => hasModule(item.module));

  const wrapperStyle = fullScreen
    ? { display: 'flex', flexDirection: 'column', width: '100%', height: '100%', background: '#f8fafc', overflow: 'hidden' }
    : { display: open ? 'flex' : 'none' };
  const dialogStyle = fullScreen
    ? { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', borderRadius: 0 }
    : {};

  return (<>
    <Box className={fullScreen ? undefined : classes.overlay} onClick={fullScreen ? undefined : onClose} style={wrapperStyle}>
      <Box className={fullScreen ? undefined : classes.dialog} onClick={fullScreen ? undefined : e => e.stopPropagation()} style={dialogStyle}>
        {/* ──── Header ──── */}
        {fullScreen ? (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 24px', height: 52, background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
            <IconButton size="small" onClick={onClose} style={{ color: '#64748b' }}>
              <ChevronLeftIcon style={{ fontSize: 18 }} />
            </IconButton>
            <Box style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SendIcon style={{ fontSize: 16, color: '#fff' }} />
            </Box>
            <Typography style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', flex: 1 }}>
              {lang === 'pt' ? 'Operações' : 'Operations'}
            </Typography>
          </Box>
        ) : (
          <Box className={classes.header}>
            <Box className={classes.headerLeft}>
              <Box className={classes.headerLogo}>
                <DirectionsIcon style={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
                  {t(lang, 'manage')}
                </Typography>
                <Typography style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                  {t(lang, 'manageDesc')}
                </Typography>
              </Box>
            </Box>
            <Button size="small" onClick={onClose} startIcon={<CloseIcon style={{ fontSize: 13 }} />}
              style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'none', fontWeight: 500, borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', padding: '4px 14px', background: 'rgba(255,255,255,0.06)', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#94a3b8'; }}>
              Fechar
            </Button>
          </Box>
        )}

        {/* ──── Sidebar + Content layout ──── */}
        <Box className={classes.layoutRow}>
          {/* Sidebar navigation */}
          <Box className={classes.sidebar}>
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              const iconColors = {
                dispatch: { bg: '#dbeafe', fg: '#3b82f6' },
                vrp: { bg: '#ede9fe', fg: '#8b5cf6' },
                status: { bg: '#dcfce7', fg: '#22c55e' },
                locations: { bg: '#fef3c7', fg: '#f59e0b' },
              };
              const ic = iconColors[item.id] || { bg: '#f1f5f9', fg: '#64748b' };
              return (
                <Box
                  key={item.id}
                  className={`${classes.navItem} ${isActive ? classes.navItemActive : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  <Box style={{ width: 32, height: 32, borderRadius: 8, background: isActive ? ic.fg : ic.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                    <Icon style={{ fontSize: 16, color: isActive ? '#fff' : ic.fg }} />
                  </Box>
                  <span style={{ flex: 1 }}>{item.label || t(lang, item.labelKey)}</span>
                  {item.count > 0 && (
                    <span className={`${classes.navBadge} ${isActive ? classes.navBadgeActive : ''}`}>
                      {item.count}
                    </span>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* ──── Main Content ──── */}
          <Box className={classes.content}>
            {/* ════ OPERATIONS LIVE (one row per vehicle) ════ */}
            {activeSection === 'operations-live' && hasModule('route_planner') && (
              <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <OrdersOperationsView
                  lang={lang}
                  onOpenLive={(entry) => {
                    // Hand off to the planner-side LiveRoutePanel via a custom
                    // event the OrdersView listens to. We switch to the
                    // 'orders' view first so OrdersView is mounted, then
                    // dispatch a window event so it can resolve the OT and
                    // open the live panel.
                    dispatch(setActiveView('orders'));
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('openLiveForVehicle', {
                        detail: {
                          vehicleId: entry.vehicleId,
                          vehiclePlate: entry.vehiclePlate,
                          routeId: entry.routeId,
                          currentOTId: entry.currentOT?.id,
                        },
                      }));
                    }, 50);
                  }}
                />
              </Box>
            )}

            {/* ════ PAINEL DE OPERAÇÕES (cockpit mercadoria) ════ */}
            {activeSection === 'ops-board' && hasModule('ops_board') && (
              <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <OperationsBoard lang={lang} />
              </Box>
            )}

            {/* ════ ROUTES LIBRARY (embedded) ════ */}
            {activeSection === 'routes-library' && hasModule('route_planner') && (
              <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <RoutesView embedded />
              </Box>
            )}

            {/* Alarmes de velocidade — MOVIDO para o menu Frota (FleetView). */}

            {/* ════ ROUTES SECTION ════ */}
            {activeSection === 'routes' && hasModule('route_planner') && (
              <>
                {/* Content header with title */}
                <Box className={classes.contentHeader}>
                  <Typography className={classes.contentTitle}>
                    {t(lang, 'manageRoutes')}
                  </Typography>
                  <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    {savedRoutes.length} {savedRoutes.length === 1 ? 'rota' : 'rotas'}
                  </Typography>
                </Box>

                {/* Fixed search bar */}
                {savedRoutes.length > 0 && (
                  <>
                    <Box className={classes.searchBar}>
                      <TextField
                        size="small" variant="outlined" placeholder={t(lang, 'searchRoutes')}
                        value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
                          style: { fontSize: '0.78rem', height: 32 },
                        }}
                        style={{ flex: 1, minWidth: 140 }}
                      />
                      {uniqueVehicles.length > 0 && (
                        <Select
                          size="small" variant="outlined" value={filterVehicle} displayEmpty
                          onChange={e => setFilterVehicle(e.target.value)}
                          style={{ fontSize: '0.72rem', minWidth: 110, height: 32 }}
                        >
                          <MenuItem value="" style={{ fontSize: '0.78rem' }}>{t(lang, 'allVehicles')}</MenuItem>
                          {uniqueVehicles.map(v => (
                            <MenuItem key={v} value={v} style={{ fontSize: '0.78rem' }}>{v}</MenuItem>
                          ))}
                        </Select>
                      )}
                      {uniqueDrivers.length > 0 && (
                        <Select
                          size="small" variant="outlined" value={filterDriver} displayEmpty
                          onChange={e => setFilterDriver(e.target.value)}
                          style={{ fontSize: '0.72rem', minWidth: 110, height: 32 }}
                        >
                          <MenuItem value="" style={{ fontSize: '0.78rem' }}>{t(lang, 'allDrivers')}</MenuItem>
                          {uniqueDrivers.map(d => (
                            <MenuItem key={d} value={d} style={{ fontSize: '0.78rem' }}>{d}</MenuItem>
                          ))}
                        </Select>
                      )}
                      <Select
                        size="small" variant="outlined" value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                        startAdornment={<SortIcon style={{ fontSize: 14, color: '#94a3b8', marginRight: 4 }} />}
                        style={{ fontSize: '0.72rem', minWidth: 90, height: 32 }}
                      >
                        <MenuItem value="date" style={{ fontSize: '0.78rem' }}>{t(lang, 'sortByDate')}</MenuItem>
                        <MenuItem value="name" style={{ fontSize: '0.78rem' }}>{t(lang, 'sortByName')}</MenuItem>
                        <MenuItem value="distance" style={{ fontSize: '0.78rem' }}>{t(lang, 'sortByDistance')}</MenuItem>
                      </Select>
                    </Box>

                    {/* Fixed action toolbar */}
                    <Box className={classes.toolbar}>
                      <Typography style={{ fontSize: '0.7rem', color: '#64748b', flex: 1 }}>
                        {selectedRoutes.length} {t(lang, 'routesSelected')}
                        {filteredRoutes.length !== savedRoutes.length && (
                          <span style={{ color: '#94a3b8', marginLeft: 4 }}>({filteredRoutes.length}/{savedRoutes.length})</span>
                        )}
                        {selectedRoutes.length >= 5 && (
                          <span style={{ color: '#f59e0b', marginLeft: 6 }}>({t(lang, 'maxRoutesWarning')})</span>
                        )}
                      </Typography>
                      <Tooltip title={t(lang, 'showOnMap')}>
                        <span>
                          <Button size="small" variant="outlined" className={classes.actionBtn}
                            startIcon={<MapIcon style={{ fontSize: 14 }} />}
                            disabled={selectedRoutes.length === 0}
                            onClick={handleShowOnMap}
                            style={{ borderColor: '#3b82f6', color: '#3b82f6' }}>
                            {t(lang, 'showOnMap')}
                          </Button>
                        </span>
                      </Tooltip>
                      <Button size="small" variant="outlined" className={classes.actionBtn}
                        startIcon={<DeleteIcon style={{ fontSize: 14 }} />}
                        disabled={selectedRoutes.length === 0}
                        onClick={handleDeleteSelected}
                        style={{ borderColor: '#ef4444', color: '#ef4444' }}>
                        {t(lang, 'delete')}
                      </Button>
                    </Box>
                  </>
                )}

                {/* Scrollable routes list */}
                <Box className={classes.body}>
                  {savedRoutes.length === 0 ? (
                    <Box className={classes.emptyState}>
                      <DirectionsIcon style={{ fontSize: 44, color: '#cbd5e1' }} />
                      <Typography style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                        {t(lang, 'noRoutesToManage')}
                      </Typography>
                    </Box>
                  ) : filteredRoutes.length === 0 ? (
                    <Box className={classes.emptyState}>
                      <SearchIcon style={{ fontSize: 44, color: '#cbd5e1' }} />
                      <Typography style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                        {t(lang, 'noRoutesFound')}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {paginatedRoutes.map(route => {
                        const isSelected = selectedRoutes.includes(route.id);
                        const distKm = route.computedRoute?.distance
                          ? (route.computedRoute.distance / 1000).toFixed(1)
                          : null;
                        return (
                          <Box
                            key={route.id}
                            className={`${classes.routeCard} ${isSelected ? classes.routeCardSelected : ''}`}
                            onClick={() => toggleRouteSelect(route.id)}>
                            <Checkbox checked={isSelected} size="small" color="primary"
                              style={{ padding: 4 }} />
                            <Box className={classes.routeInfo}>
                              <Typography className={classes.routeName}>
                                {route.name || t(lang, 'routeNoName')}
                              </Typography>
                              <Box className={classes.routePoints}>
                                <RoomIcon style={{ fontSize: 10, color: '#22c55e' }} />
                                <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {route.origin?.name || '—'}
                                </span>
                                <span style={{ color: '#94a3b8' }}>→</span>
                                <FlagIcon style={{ fontSize: 10, color: '#ef4444' }} />
                                <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {route.destination?.name || '—'}
                                </span>
                              </Box>
                              <Box className={classes.routeMeta}>
                                {distKm && (
                                  <Chip label={`${distKm} km`} size="small" className={classes.metaChip}
                                    style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6' }} />
                                )}
                                {route.vehiclePlate && (
                                  <Chip label={route.vehiclePlate} size="small" className={classes.metaChip}
                                    style={{ background: 'rgba(34,197,94,0.08)', color: '#22c55e' }} />
                                )}
                                {route.driverName && (
                                  <Chip label={route.driverName} size="small" className={classes.metaChip}
                                    style={{ background: 'rgba(139,92,246,0.08)', color: '#8b5cf6' }} />
                                )}
                                {route.stops?.length > 0 && (
                                  <Chip label={`${route.stops.length} ${route.stops.length === 1 ? 'paragem' : 'paragens'}`} size="small" className={classes.metaChip}
                                    style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b' }} />
                                )}
                                {route.savedAt && (
                                  <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', alignSelf: 'center' }}>
                                    {formatDate(route.savedAt)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Tooltip title={t(lang, 'viewRouteDetails')}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetailRoute(route); }}
                                style={{ color: '#94a3b8', padding: 4 }}>
                                <InfoIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })}

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <Box className={classes.pagination}>
                          <IconButton size="small" disabled={currentPage <= 1}
                            onClick={() => setCurrentPage(p => p - 1)} style={{ padding: 4 }}>
                            <ChevronLeftIcon style={{ fontSize: 18 }} />
                          </IconButton>
                          <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>
                            {currentPage} {t(lang, 'pageOf')} {totalPages}
                          </Typography>
                          <IconButton size="small" disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(p => p + 1)} style={{ padding: 4 }}>
                            <ChevronRightIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </>
            )}

            {/* ════ DISPATCH SECTION ════ */}
            {activeSection === 'dispatch' && hasModule('route_planner') && (
              <React.Fragment>
                {/* Dispatch header — compact */}
                <Box style={{ padding: '10px 20px', background: 'linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)', borderBottom: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Box style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <SendIcon style={{ fontSize: 14, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>
                        {lang === 'pt' ? 'Despacho' : 'Dispatch'}
                      </Typography>
                      <Typography style={{ fontSize: '0.55rem', color: '#64748b' }}>
                        {lang === 'pt' ? 'Arraste motoristas/veículos para as rotas' : 'Drag drivers/vehicles onto routes'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box style={{ display: 'flex', gap: 8 }}>
                    {Object.keys(dispatchMap).length > 0 && (
                      <Button size="small" variant="contained"
                        onClick={handleConfirmDispatch} disabled={dispatching}
                        startIcon={dispatching ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <SendIcon style={{ fontSize: 14 }} />}
                        style={{ fontSize: '0.7rem', fontWeight: 700, background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', borderRadius: 10, textTransform: 'none', padding: '5px 16px' }}>
                        {lang === 'pt' ? 'Confirmar Despacho' : 'Confirm Dispatch'}
                      </Button>
                    )}
                  </Box>
                </Box>
                <Box style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                  {/* ═══ LEFT: Motoristas/Veículos (draggable) ═══ */}
                  <Box style={{ flex: '0 0 35%', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fafbfc' }}>
                    <Box style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 6, background: '#fff' }}>
                      <PersonIcon style={{ fontSize: 14, color: '#f59e0b' }} />
                      <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#92400e' }}>
                        {lang === 'pt' ? 'Motoristas / Veículos' : 'Drivers / Vehicles'}
                      </Typography>
                      <Box style={{ flex: 1 }} />
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#f1f5f9', borderRadius: 6, padding: '3px 8px' }}>
                        <SearchIcon style={{ fontSize: 11, color: '#94a3b8' }} />
                        <input placeholder="Filtrar..." value={dispatchDriverFilter}
                          onChange={e => { setDispatchDriverFilter(e.target.value); setDriverPage(1); }}
                          style={{ border: 'none', outline: 'none', width: 55, fontSize: '0.6rem', color: '#334155', background: 'transparent' }} />
                      </Box>
                    </Box>
                    <Box style={{ flex: 1, overflowY: 'auto' }}>
                      {drivers.filter(d => !dispatchDriverFilter || d.name.toLowerCase().includes(dispatchDriverFilter.toLowerCase()))
                        .slice((driverPage - 1) * DRIVERS_PER_PAGE, driverPage * DRIVERS_PER_PAGE)
                        .map(driver => {
                        const vehicleForDriver = vehicles.find(v => v.currentDriver?.id === driver.id);
                        const assignedRoutes = dispatchMap[driver.id] || [];
                        return (
                          <Box key={driver.id} draggable onDragStart={e => handleDragStartDriver(e, driver.id)}
                            style={{ margin: '3px 6px', padding: '7px 10px', borderRadius: 8, background: '#fff', border: '1px solid #e2e8f0', cursor: 'grab', transition: 'all 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Box style={{ width: 8, height: 8, borderRadius: '50%', background: vehicleForDriver ? '#22c55e' : '#94a3b8', flexShrink: 0 }} />
                              <Typography style={{ fontSize: '0.68rem', fontWeight: 600, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {driver.name}
                              </Typography>
                            </Box>
                            {vehicleForDriver && (
                              <Typography style={{ fontSize: '0.52rem', color: '#3b82f6', fontWeight: 600, marginLeft: 14 }}>
                                🚛 {vehicleForDriver.plate || vehicleForDriver.name}
                              </Typography>
                            )}
                            {assignedRoutes.length > 0 && assignedRoutes.map(rid => {
                              const r = savedRoutes.find(sr => sr.id === rid);
                              return (
                                <Box key={rid} style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 3, marginLeft: 14, padding: '2px 6px', background: '#dcfce7', borderRadius: 4 }}>
                                  <Typography style={{ fontSize: '0.5rem', fontWeight: 600, color: '#16a34a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r?.name || rid}</Typography>
                                  <Box onClick={() => handleRemoveFromDispatch(driver.id, rid)} style={{ cursor: 'pointer', fontSize: 10, color: '#94a3b8' }}>✕</Box>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Pagination */}
                    {(() => {
                      const filtered = drivers.filter(d => !dispatchDriverFilter || d.name.toLowerCase().includes(dispatchDriverFilter.toLowerCase()));
                      const totalPages = Math.max(1, Math.ceil(filtered.length / DRIVERS_PER_PAGE));
                      if (totalPages <= 1) return null;
                      return (
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '4px 8px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
                          <Button size="small" disabled={driverPage <= 1} onClick={() => setDriverPage(p => p - 1)} style={{ fontSize: '0.6rem', minWidth: 24, padding: '2px 4px' }}>←</Button>
                          <Typography style={{ fontSize: '0.58rem', color: '#64748b' }}>{driverPage}/{totalPages}</Typography>
                          <Button size="small" disabled={driverPage >= totalPages} onClick={() => setDriverPage(p => p + 1)} style={{ fontSize: '0.6rem', minWidth: 24, padding: '2px 4px' }}>→</Button>
                          <Typography style={{ fontSize: '0.5rem', color: '#94a3b8' }}>{filtered.length} total</Typography>
                        </Box>
                      );
                    })()}
                  </Box>

                  {/* ═══ RIGHT: OTs/Rotas (drop zone) + Despachadas ═══ */}
                  <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Por despachar */}
                    <Box style={{ padding: '6px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <DirectionsIcon style={{ fontSize: 14, color: '#3b82f6' }} />
                      <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e40af' }}>
                        {lang === 'pt' ? 'Rotas por despachar' : 'Routes to dispatch'}
                      </Typography>
                      <Box style={{ flex: 1 }} />
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#f1f5f9', borderRadius: 6, padding: '3px 8px' }}>
                        <SearchIcon style={{ fontSize: 11, color: '#94a3b8' }} />
                        <input placeholder="Filtrar..." value={dispatchRouteFilter}
                          onChange={e => setDispatchRouteFilter(e.target.value)}
                          style={{ border: 'none', outline: 'none', width: 60, fontSize: '0.6rem', color: '#334155', background: 'transparent' }} />
                      </Box>
                    </Box>
                    <Box style={{ flex: 1, overflowY: 'auto', minHeight: 120 }}>
                      {savedRoutes.filter(r => !r.sentToVehicle && r.dbStatus !== 'completed' && r.dbStatus !== 'cancelled')
                        .filter(r => !dispatchRouteFilter || (r.name || '').toLowerCase().includes(dispatchRouteFilter.toLowerCase()) || (r.origin?.name || '').toLowerCase().includes(dispatchRouteFilter.toLowerCase()))
                        .map(route => {
                        const isAssigned = Object.values(dispatchMap).flat().includes(route.id);
                        const isDropOver = dragOverRoute === route.id;
                        const distKm = route.computedRoute?.distance ? Math.round(route.computedRoute.distance / 1000) : null;
                        return (
                          <Box key={route.id}
                            onDragOver={e => { e.preventDefault(); setDragOverRoute(route.id); }}
                            onDragLeave={() => setDragOverRoute(null)}
                            onDrop={e => handleDropOnRoute(e, route.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                              borderBottom: '1px solid #f1f5f9',
                              borderLeft: isAssigned ? '3px solid #10b981' : '3px solid transparent',
                              background: isDropOver ? 'rgba(59,130,246,0.06)' : 'transparent',
                              outline: isDropOver ? '2px dashed #3b82f6' : 'none',
                              transition: 'all 0.15s',
                            }}>
                            <DirectionsIcon style={{ fontSize: 12, color: isAssigned ? '#10b981' : '#94a3b8' }} />
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {route.name || 'Sem nome'}
                              </Typography>
                              <Typography style={{ fontSize: '0.55rem', color: '#64748b' }}>
                                {route.origin?.name?.split(',')[0] || '?'} → {route.destination?.name?.split(',')[0] || '?'}
                              </Typography>
                            </Box>
                            {distKm && <Typography style={{ fontSize: '0.55rem', color: '#94a3b8' }}>{distKm}km</Typography>}
                            {isAssigned && <Box style={{ fontSize: '0.46rem', fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '1px 5px', borderRadius: 4 }}>ATRIBUÍDA</Box>}
                          </Box>
                        );
                      })}
                      {savedRoutes.filter(r => !r.sentToVehicle && r.dbStatus !== 'completed' && r.dbStatus !== 'cancelled').length === 0 && (
                        <Box style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>
                          <Typography style={{ fontSize: '0.72rem' }}>{lang === 'pt' ? 'Sem rotas por despachar' : 'No routes to dispatch'}</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Separador */}
                    <Box style={{ padding: '6px 12px', background: '#f0fdf4', borderTop: '1px solid #dcfce7', borderBottom: '1px solid #dcfce7', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CheckCircleIcon style={{ fontSize: 14, color: '#22c55e' }} />
                      <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#166534' }}>
                        {lang === 'pt' ? 'Despachadas / Em Trânsito' : 'Dispatched / In Transit'}
                      </Typography>
                      <Typography style={{ fontSize: '0.5rem', background: '#dcfce7', padding: '1px 6px', borderRadius: 8, fontWeight: 700, color: '#16a34a' }}>
                        {savedRoutes.filter(r => r.sentToVehicle || r.dbStatus === 'planned' || r.dbStatus === 'in_progress').length}
                      </Typography>
                    </Box>
                    <Box style={{ flex: 1, overflowY: 'auto', background: '#fafbfc' }}>
                      {savedRoutes.filter(r => r.sentToVehicle || r.dbStatus === 'planned' || r.dbStatus === 'in_progress')
                        .map(route => {
                        const sc = { draft: '#94a3b8', planned: '#3b82f6', in_progress: '#f59e0b', completed: '#22c55e', cancelled: '#ef4444' }[route.dbStatus] || '#94a3b8';
                        const sl = { draft: 'Rascunho', planned: 'Atribuída', in_progress: 'Em Trânsito', completed: 'Concluída', cancelled: 'Cancelada' }[route.dbStatus] || '?';
                        const distKm = route.computedRoute?.distance ? Math.round(route.computedRoute.distance / 1000) : null;
                        return (
                          <Box key={route.id} style={{ margin: '3px 8px', padding: '7px 10px', borderRadius: 8, background: '#fff', border: '1px solid #e2e8f0', borderLeft: `3px solid ${sc}` }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {route.name || 'Sem nome'}
                              </Typography>
                              <Box style={{ fontSize: '0.45rem', fontWeight: 700, background: `${sc}15`, color: sc, padding: '1px 5px', borderRadius: 4 }}>{sl}</Box>
                            </Box>
                            <Typography style={{ fontSize: '0.52rem', color: '#64748b' }}>
                              {route.origin?.name?.split(',')[0] || '?'} → {route.destination?.name?.split(',')[0] || '?'}
                            </Typography>
                            <Box style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap' }}>
                              {(() => {
                                // Vehicle chip — show plate; if the EcooGPS
                                // device name differs from the licence plate,
                                // append the friendly name (same rule as the
                                // Orders → Veículo column).
                                const plate = route.vehiclePlate || route.routeData?.vehicleInfo?.plate;
                                const vehName = route.vehicle?.name || route.routeData?.vehicleInfo?.name;
                                if (!plate) return null;
                                const showName = vehName && vehName !== plate;
                                return (
                                  <Box style={{ fontSize: '0.45rem', fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '1px 4px', borderRadius: 3 }}>
                                    🚛 {plate}{showName ? ` · ${vehName}` : ''}
                                  </Box>
                                );
                              })()}
                              {(route.driverName || route.routeData?.driverInfo?.name) && (
                                <Box style={{ fontSize: '0.45rem', fontWeight: 700, background: '#eff6ff', color: '#2563eb', padding: '1px 4px', borderRadius: 3 }}>👤 {route.driverName || route.routeData?.driverInfo?.name}</Box>
                              )}
                              {distKm && <Box style={{ fontSize: '0.45rem', color: '#94a3b8' }}>{distKm}km</Box>}
                              {/* Execution date: prefer actualStartedAt (when
                                  the driver pressed Iniciar) over the planned
                                  departureTime — operators care about reality. */}
                              {route.actualStartedAt
                                ? <Box style={{ fontSize: '0.45rem', color: '#16a34a', fontWeight: 600 }}>▶ {new Date(route.actualStartedAt).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Box>
                                : (route.departureTime && <Box style={{ fontSize: '0.45rem', color: '#94a3b8' }}>{new Date(route.departureTime).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Box>)}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            )}

            {/* ════ STATUS / PLANEADO VS REAL SECTION ════ */}
            {activeSection === 'status' && hasModule('route_planner') && (() => {
              // Show all operational routes — a route counts as operational when it has been
              // dispatched (sentToVehicle) OR when its current dbStatus is planned/in_progress
              // (i.e. it was started via an OT flow without the explicit dispatch step).
              const dispatchedRoutes = savedRoutes.filter(r => {
                if (statusFilter === 'active') return r.dbStatus === 'planned' || r.dbStatus === 'in_progress';
                return true;
              });

              const kanbanColumns = [
                { key: 'draft', label: lang === 'pt' ? 'Rascunho' : 'Draft', color: '#94a3b8' },
                { key: 'planned', label: lang === 'pt' ? 'Atribuída' : 'Assigned', color: '#3b82f6' },
                { key: 'in_progress', label: lang === 'pt' ? 'Em Trânsito' : 'In Transit', color: '#f59e0b' },
                { key: 'completed', label: lang === 'pt' ? 'Concluída' : 'Completed', color: '#22c55e' },
              ];

              return (
                <>
                  <Box className={classes.contentHeader}>
                    <Typography className={classes.contentTitle}>
                      <TimelineIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6, color: '#6366f1' }} />
                      {lang === 'pt' ? 'Estado das Rotas' : 'Route Status'}
                    </Typography>
                    <Box style={{ display: 'flex', gap: 4 }}>
                      {['active', 'all', 'kanban', 'history'].map(f => (
                        <Button key={f} size="small" variant={statusFilter === f ? 'contained' : 'outlined'}
                          onClick={() => setStatusFilter(f)}
                          style={{
                            fontSize: '0.68rem', textTransform: 'none', borderRadius: 8, padding: '3px 10px',
                            background: statusFilter === f ? '#6366f1' : 'transparent',
                            color: statusFilter === f ? '#fff' : '#6366f1',
                            borderColor: '#6366f1',
                          }}>
                          {f === 'active' ? (lang === 'pt' ? 'Ativas' : 'Active')
                            : f === 'all' ? (lang === 'pt' ? 'Todas' : 'All')
                            : f === 'kanban' ? (lang === 'pt' ? 'Quadro de Tráfego' : 'Traffic Board')
                            : (lang === 'pt' ? 'Histórico' : 'History')}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {/* History view — chronological timeline of OT events from the audit log */}
                  {statusFilter === 'history' ? (() => {
                    // Use real backend events (transport_order_events table)
                    const events = otEvents.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                    const cfg = {
                      created:           { label: lang === 'pt' ? 'OT criada' : 'Order created',           color: '#64748b', icon: '✎' },
                      vehicle_assigned:  { label: lang === 'pt' ? 'Veículo atribuído' : 'Vehicle assigned', color: '#3b82f6', icon: '🚛' },
                      route_linked:      { label: lang === 'pt' ? 'Rota associada' : 'Route linked',       color: '#6366f1', icon: '🔗' },
                      started:           { label: lang === 'pt' ? 'Iniciada' : 'Started',                  color: '#059669', icon: '▶' },
                      delivered:         { label: lang === 'pt' ? 'Entregue' : 'Delivered',                color: '#047857', icon: '✔' },
                      cancelled:         { label: lang === 'pt' ? 'Cancelada' : 'Cancelled',               color: '#dc2626', icon: '✕' },
                      status_change:     { label: lang === 'pt' ? 'Estado alterado' : 'Status changed',    color: '#f59e0b', icon: '↻' },
                      note:              { label: lang === 'pt' ? 'Nota' : 'Note',                        color: '#94a3b8', icon: '📝' },
                    };

                    // Group by day
                    const groups = {};
                    events.forEach(ev => {
                      const d = new Date(ev.createdAt);
                      const key = d.toLocaleDateString(lang === 'pt' ? 'pt-PT' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                      if (!groups[key]) groups[key] = [];
                      groups[key].push(ev);
                    });
                    const dayKeys = Object.keys(groups);

                    return (
                      <Box className={classes.body} style={{ padding: '12px 16px' }}>
                        {otEventsLoading ? (
                          <Box className={classes.emptyState}>
                            <CircularProgress size={32} />
                            <Typography style={{ fontSize: '0.74rem', color: '#94a3b8', marginTop: 8 }}>
                              {lang === 'pt' ? 'A carregar histórico…' : 'Loading history…'}
                            </Typography>
                          </Box>
                        ) : events.length === 0 ? (
                          <Box className={classes.emptyState}>
                            <HistoryIcon style={{ fontSize: 44, color: '#cbd5e1' }} />
                            <Typography style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                              {lang === 'pt' ? 'Sem eventos registados' : 'No events recorded'}
                            </Typography>
                            <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', maxWidth: 380, marginTop: 4 }}>
                              {lang === 'pt'
                                ? 'O histórico mostra todas as transições das OTs: criação, atribuição de veículo, associação de rota, início, entrega e cancelamento.'
                                : 'History shows all OT transitions: creation, vehicle assignment, route link, start, delivery and cancellation.'}
                            </Typography>
                          </Box>
                        ) : (
                          <Box style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            <Box style={{
                              padding: '8px 12px', background: '#eef2ff',
                              borderLeft: '3px solid #6366f1', borderRadius: 4,
                            }}>
                              <Typography style={{ fontSize: '0.68rem', color: '#4338ca', fontWeight: 600 }}>
                                {events.length} {lang === 'pt' ? 'eventos · timeline cronológica das ordens de transporte' : 'events · chronological order timeline'}
                              </Typography>
                            </Box>
                            {dayKeys.map(day => (
                              <Box key={day}>
                                <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, paddingLeft: 4 }}>
                                  {day}
                                </Typography>
                                <Box style={{ position: 'relative', paddingLeft: 18 }}>
                                  <Box style={{ position: 'absolute', top: 4, bottom: 4, left: 7, width: 2, background: '#e2e8f0' }} />
                                  {groups[day].map((ev) => {
                                    const c = cfg[ev.eventType] || cfg.note;
                                    return (
                                      <Box key={ev.id} style={{
                                        position: 'relative', marginBottom: 10,
                                        display: 'flex', gap: 10, alignItems: 'flex-start',
                                      }}>
                                        <Box style={{
                                          position: 'absolute', left: -18, top: 6,
                                          width: 12, height: 12, borderRadius: '50%',
                                          background: c.color, border: '2px solid #fff',
                                          boxShadow: `0 0 0 2px ${c.color}35`,
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                          color: '#fff', fontSize: 7, fontWeight: 800,
                                        }}>{c.icon}</Box>
                                        <Box style={{
                                          flex: 1, background: '#fff', border: '1px solid #e2e8f0',
                                          borderRadius: 8, padding: '8px 12px',
                                        }}>
                                          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
                                              <span style={{
                                                fontSize: '0.56rem', fontWeight: 800, color: c.color,
                                                background: `${c.color}18`, borderRadius: 10,
                                                padding: '1px 8px', textTransform: 'uppercase', letterSpacing: '0.04em',
                                              }}>{c.label}</span>
                                              <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#1e40af' }}>
                                                {ev.orderNumber}
                                              </Typography>
                                              {ev.routeName && (
                                                <Typography style={{ fontSize: '0.62rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                                                  · {ev.routeName}
                                                </Typography>
                                              )}
                                            </Box>
                                            <Typography style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                              {new Date(ev.createdAt).toLocaleTimeString(lang === 'pt' ? 'pt-PT' : 'en-GB', { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                          </Box>
                                          {ev.notes && (
                                            <Typography style={{ fontSize: '0.66rem', color: '#475569', marginBottom: 3 }}>
                                              {ev.notes}
                                            </Typography>
                                          )}
                                          <Box style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {ev.vehiclePlate && (
                                              <span style={{ fontSize: '0.56rem', color: '#16a34a', background: '#dcfce7', borderRadius: 6, padding: '1px 6px', fontWeight: 600 }}>🚛 {ev.vehiclePlate}</span>
                                            )}
                                            {ev.driverName && (
                                              <span style={{ fontSize: '0.56rem', color: '#1d4ed8', background: '#dbeafe', borderRadius: 6, padding: '1px 6px', fontWeight: 600 }}>👤 {ev.driverName}</span>
                                            )}
                                            {ev.estimatedCost != null && (
                                              <span style={{ fontSize: '0.56rem', color: '#15803d', background: '#dcfce7', borderRadius: 6, padding: '1px 6px', fontWeight: 600 }}>💶 €{Number(ev.estimatedCost).toFixed(2)}</span>
                                            )}
                                            {ev.distanceKm != null && (
                                              <span style={{ fontSize: '0.56rem', color: '#64748b', background: '#f1f5f9', borderRadius: 6, padding: '1px 6px' }}>📏 {Number(ev.distanceKm).toFixed(1)} km</span>
                                            )}
                                            {ev.userName && (
                                              <span style={{ fontSize: '0.56rem', color: '#94a3b8', marginLeft: 'auto' }}>por {ev.userName}</span>
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })() : /* Kanban view */
                  statusFilter === 'kanban' ? (
                    <Box style={{ display: 'flex', gap: 10, flex: 1, overflow: 'auto', padding: '8px 12px' }}>
                      {kanbanColumns.map(col => {
                        const colRoutes = savedRoutes.filter(r => {
                          const s = r.dbStatus || r.routeStatus || 'draft';
                          return s === col.key || (col.key === 'draft' && (s === 'idle' || s === 'draft'));
                        });
                        return (
                          <Box key={col.key} style={{ flex: '1 1 25%', minWidth: 180, display: 'flex', flexDirection: 'column' }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: `2px solid ${col.color}`, marginBottom: 6 }}>
                              <Box style={{ width: 8, height: 8, borderRadius: '50%', background: col.color }} />
                              <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', flex: 1 }}>{col.label}</Typography>
                              <Chip label={colRoutes.length} size="small" style={{ height: 16, fontSize: '0.52rem', fontWeight: 700, background: `${col.color}20`, color: col.color }} />
                            </Box>
                            <Box style={{ flex: 1, overflowY: 'auto' }}>
                              {colRoutes.map(route => {
                                const dbId = route.dbId || (typeof route.id === 'string' ? parseInt(route.id.replace('api-', '')) : route.id);
                                const ots = routeOTs[dbId] || [];
                                return (
                                  <Box key={route.id} style={{
                                    padding: '8px 10px', borderRadius: 8, marginBottom: 6,
                                    background: '#fff', border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                  }}>
                                    <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {route.name || 'Sem nome'}
                                    </Typography>
                                    <Typography style={{ fontSize: '0.58rem', color: '#64748b', marginBottom: 3 }}>
                                      {route.origin?.name?.split(',')[0] || '?'} → {route.destination?.name?.split(',')[0] || '?'}
                                    </Typography>
                                    <Box style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                      {route.routeDrivers?.map(rd => (
                                        <Chip key={rd.driverId} label={rd.driverName?.split(' ')[0] || '?'} size="small"
                                          style={{ height: 14, fontSize: '0.48rem', fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }} />
                                      ))}
                                      {ots.length > 0 && (
                                        <Chip label={`${ots.length} OT`} size="small"
                                          style={{ height: 14, fontSize: '0.48rem', fontWeight: 700, background: '#cffafe', color: '#0891b2' }} />
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                              {colRoutes.length === 0 && (
                                <Typography style={{ fontSize: '0.68rem', color: '#cbd5e1', textAlign: 'center', padding: 16, fontStyle: 'italic' }}>
                                  {lang === 'pt' ? 'Vazio' : 'Empty'}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                  <Box className={classes.body}>
                    {dispatchedRoutes.length === 0 ? (
                      <Box className={classes.emptyState}>
                        <TimelineIcon style={{ fontSize: 44, color: '#cbd5e1' }} />
                        <Typography style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                          {lang === 'pt' ? 'Sem rotas despachadas' : 'No dispatched routes'}
                        </Typography>
                      </Box>
                    ) : dispatchedRoutes.map(route => {
                      const sc = getStatusConfig(route.dbStatus);
                      const isUpdating = updatingStatus === route.dbId;
                      const timeDiff = formatTimeDiff(route.departureTime, route.actualStartedAt);
                      const isHistoryExpanded = expandedHistory === route.dbId;
                      const isReDispatching = reDispatchRoute === route.dbId;

                      // Progress bar steps
                      const statusSteps = [
                        { key: 'draft', label: 'Rascunho', color: '#64748b' },
                        { key: 'planned', label: 'Planeada', color: '#1d4ed8' },
                        { key: 'in_progress', label: 'Em Trânsito', color: '#059669' },
                        { key: 'completed', label: 'Concluída', color: '#047857' },
                      ];
                      const cancelledStep = { key: 'cancelled', label: 'Cancelada', color: '#dc2626' };
                      const isCancelled = route.dbStatus === 'cancelled';
                      const currentStepIdx = isCancelled ? -1 : statusSteps.findIndex(s => s.key === route.dbStatus);

                      return (
                        <Box key={route.id} style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9' }}>
                          {/* Route Status Progress Bar */}
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 10, padding: '6px 0' }}>
                            {statusSteps.map((step, idx) => {
                              const isActive = isCancelled ? false : idx <= currentStepIdx;
                              const isCurrent = !isCancelled && idx === currentStepIdx;
                              return (
                                <React.Fragment key={step.key}>
                                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                                    <Box style={{
                                      width: isCurrent ? 14 : 10, height: isCurrent ? 14 : 10, borderRadius: '50%',
                                      background: isActive ? step.color : '#e2e8f0',
                                      border: isCurrent ? `2px solid ${step.color}` : 'none',
                                      boxShadow: isCurrent ? `0 0 0 3px ${step.color}25` : 'none',
                                      transition: 'all 0.2s',
                                    }} />
                                    <Typography style={{
                                      fontSize: '0.52rem', color: isActive ? step.color : '#94a3b8',
                                      fontWeight: isCurrent ? 700 : 500, marginTop: 3, whiteSpace: 'nowrap',
                                    }}>
                                      {step.label}
                                    </Typography>
                                  </Box>
                                  {idx < statusSteps.length - 1 && (
                                    <Box style={{
                                      flex: 1, height: 2, marginTop: -10,
                                      background: (!isCancelled && idx < currentStepIdx) ? statusSteps[idx + 1].color : '#e2e8f0',
                                      transition: 'background 0.2s',
                                    }} />
                                  )}
                                </React.Fragment>
                              );
                            })}
                            {isCancelled && (
                              <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: 8 }}>
                                <Box style={{
                                  width: 14, height: 14, borderRadius: '50%',
                                  background: cancelledStep.color,
                                  border: `2px solid ${cancelledStep.color}`,
                                  boxShadow: `0 0 0 3px ${cancelledStep.color}25`,
                                }} />
                                <Typography style={{ fontSize: '0.52rem', color: cancelledStep.color, fontWeight: 700, marginTop: 3 }}>
                                  {cancelledStep.label}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Row 1: name + status chip */}
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a', flex: 1 }}>
                              {route.name || `${route.origin?.name?.split(',')[0]} → ${route.destination?.name?.split(',')[0]}`}
                            </Typography>
                            <Box style={{ background: sc.bg, color: sc.color, fontSize: '0.62rem', fontWeight: 700, borderRadius: 20, padding: '2px 8px' }}>
                              {sc.label}
                            </Box>
                          </Box>

                          {/* Row 2: origin → dest */}
                          <Box style={{ display: 'flex', gap: 6, fontSize: '0.68rem', color: '#64748b', marginBottom: 4, flexWrap: 'wrap' }}>
                            <span><RoomIcon style={{ fontSize: 10, color: '#22c55e', verticalAlign: 'middle' }} /> {route.origin?.name?.split(',')[0] || '—'}</span>
                            <span>→</span>
                            <span><FlagIcon style={{ fontSize: 10, color: '#ef4444', verticalAlign: 'middle' }} /> {route.destination?.name?.split(',')[0] || '—'}</span>
                          </Box>

                          {/* Row 2b: vehicle + driver + distance + duration */}
                          <Box style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                            {(route.vehiclePlate || route.routeData?.vehicleInfo?.plate) && (
                              <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.58rem', fontWeight: 600, background: '#dcfce7', color: '#16a34a', padding: '1px 8px', borderRadius: 6 }}>
                                🚛 {route.vehiclePlate || route.routeData?.vehicleInfo?.plate}
                              </Box>
                            )}
                            {(route.driverName || route.routeData?.driverInfo?.name) && (
                              <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.58rem', fontWeight: 600, background: '#eff6ff', color: '#2563eb', padding: '1px 8px', borderRadius: 6 }}>
                                👤 {route.driverName || route.routeData?.driverInfo?.name}
                                {driverIdOf(route) && (
                                  <Tooltip title={lang === 'pt' ? 'Editar motorista' : 'Edit driver'}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDriver(route); }} style={{ padding: 0, marginLeft: 2 }}>
                                      <EditIcon style={{ fontSize: 12, color: '#2563eb' }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                            {route.computedRoute?.distance && (
                              <Box style={{ fontSize: '0.58rem', fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '1px 8px', borderRadius: 6 }}>
                                {Math.round(route.computedRoute.distance / 1000)} km
                              </Box>
                            )}
                            {route.computedRoute?.duration && (
                              <Box style={{ fontSize: '0.58rem', fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '1px 8px', borderRadius: 6 }}>
                                {Math.floor(route.computedRoute.duration / 3600)}h{Math.round((route.computedRoute.duration % 3600) / 60)}min
                              </Box>
                            )}
                            {route.stops?.length > 0 && (
                              <Box style={{ fontSize: '0.58rem', fontWeight: 600, color: '#f59e0b', background: '#fffbeb', padding: '1px 8px', borderRadius: 6 }}>
                                {route.stops.length} {lang === 'pt' ? 'paragem(ns)' : 'stop(s)'}
                              </Box>
                            )}
                          </Box>

                          {/* Row 3: Planned vs Real times */}
                          <Box style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lang === 'pt' ? 'Partida prevista' : 'Planned departure'}</Typography>
                              <Typography style={{ fontSize: '0.72rem', color: '#334155', fontWeight: 500 }}>{formatDateTime(route.departureTime) || '—'}</Typography>
                            </Box>
                            {route.actualStartedAt && (
                              <Box>
                                <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lang === 'pt' ? 'Início real' : 'Actual start'}</Typography>
                                <Typography style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 600 }}>{formatDateTime(route.actualStartedAt)}</Typography>
                              </Box>
                            )}
                            {route.actualCompletedAt && (
                              <Box>
                                <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lang === 'pt' ? 'Conclusão real' : 'Actual finish'}</Typography>
                                <Typography style={{ fontSize: '0.72rem', color: '#047857', fontWeight: 600 }}>{formatDateTime(route.actualCompletedAt)}</Typography>
                              </Box>
                            )}
                            {timeDiff && (
                              <Box style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <Typography style={{
                                  fontSize: '0.68rem', fontWeight: 700, borderRadius: 8, padding: '2px 8px',
                                  background: timeDiff.includes('atraso') || timeDiff.includes('late') ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                                  color: timeDiff.includes('atraso') || timeDiff.includes('late') ? '#dc2626' : '#059669',
                                }}>
                                  {timeDiff}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Row 4: history button only — action buttons (Iniciar/Cancelar/Re-despachar)
                              moved to the inline OT detail panel in OrdersView */}
                          <Box style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Button size="small" variant="outlined"
                              startIcon={isHistoryExpanded ? <ExpandLessIcon style={{ fontSize: 13 }} /> : <HistoryIcon style={{ fontSize: 13 }} />}
                              onClick={() => handleToggleHistory(route.dbId)}
                              style={{ fontSize: '0.7rem', textTransform: 'none', borderColor: '#6366f1', color: '#6366f1', borderRadius: 8, marginLeft: 'auto' }}>
                              {lang === 'pt' ? 'Histórico' : 'History'}
                            </Button>
                          </Box>

                          {/* Dispatch History panel */}
                          {isHistoryExpanded && (
                            <Box style={{
                              marginTop: 8, background: '#f8fafc',
                              border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden',
                            }}>
                              <DispatchHistoryPanel
                                history={historyData[route.dbId] || []}
                                loading={historyLoading && expandedHistory === route.dbId}
                                lang={lang}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  )}
                </>
              );
            })()}

            {/* ════ VRP / OPTIMISATION SECTION ════ */}
            {activeSection === 'vrp' && (
              <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: 40, gap: 16 }}>
                <Box style={{ width: 64, height: 64, borderRadius: 16, background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(139,92,246,0.3)' }}>
                  <TuneIcon style={{ fontSize: 28, color: '#fff' }} />
                </Box>
                <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
                  {lang === 'pt' ? 'Optimização de Rotas' : 'Route Optimization'}
                </Typography>
                <Typography style={{ fontSize: '0.82rem', color: '#64748b', textAlign: 'center', maxWidth: 360, lineHeight: 1.5 }}>
                  {lang === 'pt'
                    ? 'Esta funcionalidade está em desenvolvimento. Permitirá optimizar automaticamente a atribuição de múltiplas OTs a veículos, calculando as rotas mais eficientes.'
                    : 'This feature is under development. It will allow automatic optimization of multiple OT assignments to vehicles, calculating the most efficient routes.'}
                </Typography>
                <Box style={{ padding: '8px 16px', background: '#faf5ff', borderRadius: 10, border: '1px solid #ede9fe', marginTop: 8 }}>
                  <Typography style={{ fontSize: '0.68rem', color: '#7c3aed', fontWeight: 600 }}>
                    {lang === 'pt' ? 'Em breve: Google OR-Tools + algoritmos VRP' : 'Coming soon: Google OR-Tools + VRP algorithms'}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ════ LOCATIONS SECTION (unified) ════ */}
            {activeSection === 'locations' && (
              <ManageFavoritesDialog open={true} onClose={() => {}} embedded />
            )}

            {/* OLD LOCATIONS SECTION — kept for reference, hidden */}
            {activeSection === '__locations_old__' && (
              <>
                {/* Content header */}
                <Box className={classes.contentHeader}>
                  <Typography className={classes.contentTitle}>
                    {t(lang, 'manageLocations')}
                  </Typography>
                  <Button size="small" variant="contained" className={classes.actionBtn}
                    startIcon={<AddIcon style={{ fontSize: 14 }} />}
                    onClick={() => setShowAddForm(!showAddForm)}
                    style={{ background: '#3b82f6', color: '#fff' }}>
                    {t(lang, 'addLocation')}
                  </Button>
                </Box>

                <Box className={classes.body}>
                  {/* Add form */}
                  {showAddForm && (
                    <Box className={classes.addForm}>
                      {/* Row 1: Nome + Tipo */}
                      <Box className={classes.formRow}>
                        <TextField size="small" variant="outlined" label={t(lang, 'locationName')}
                          value={newLocation.name} fullWidth
                          onChange={e => setNewLocation({ ...newLocation, name: e.target.value })}
                          InputProps={{ style: { fontSize: '0.82rem' } }}
                          InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
                        <Select size="small" variant="outlined" value={newLocation.type}
                          onChange={e => setNewLocation({ ...newLocation, type: e.target.value })}
                          style={{ minWidth: 140, fontSize: '0.82rem', height: 40 }}>
                          {LOCATION_TYPES.map(lt => (
                            <MenuItem key={lt.value} value={lt.value} style={{ fontSize: '0.82rem' }}>
                              {getTypeLabel(lang, lt.value)}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>

                      {/* Row 2: Pesquisa de morada com autocomplete */}
                      <Box className={classes.formRow}>
                        <Autocomplete
                          fullWidth freeSolo
                          options={addressOptions}
                          getOptionLabel={opt => (typeof opt === 'string' ? opt : opt.name || '')}
                          filterOptions={x => x}
                          loading={addressLoading}
                          loadingText={t(lang, 'searching')}
                          noOptionsText={addressInput.length >= 2 ? t(lang, 'noResults') : t(lang, 'typeToSearch')}
                          onInputChange={(_, val, reason) => { if (reason !== 'reset') setAddressInput(val); }}
                          onChange={handleAddressSelect}
                          renderOption={(option) => {
                            const sourceColors = { mapbox: '#3b82f6', here: '#22c55e', osm: '#f59e0b' };
                            const sourceLabels = { mapbox: 'MB', here: 'HERE', osm: 'OSM' };
                            return (
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                                <PlaceIcon style={{ fontSize: 14, color: sourceColors[option.source] || '#3b82f6', flexShrink: 0 }} />
                                <Typography style={{ fontSize: '0.78rem', color: '#334155', flex: 1 }}>{option.name}</Typography>
                                {option.source && (
                                  <span style={{
                                    fontSize: '0.55rem', fontWeight: 700, color: sourceColors[option.source] || '#94a3b8',
                                    background: `${sourceColors[option.source] || '#94a3b8'}15`,
                                    padding: '1px 5px', borderRadius: 4, flexShrink: 0,
                                  }}>
                                    {sourceLabels[option.source] || option.source}
                                  </span>
                                )}
                              </Box>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small" variant="outlined"
                              label={t(lang, 'searchAddress')}
                              placeholder={t(lang, 'searchAddressPlaceholder')}
                              InputLabelProps={{ ...params.InputLabelProps, style: { fontSize: '0.75rem' } }}
                              InputProps={{
                                ...params.InputProps,
                                style: { fontSize: '0.82rem' },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <>
                                    {addressLoading ? <CircularProgress size={14} style={{ color: '#94a3b8' }} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>

                      {/* Coordenadas preenchidas (feedback visual) */}
                      {newLocation.latitude && newLocation.longitude && (
                        <Box style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
                          borderRadius: 6, marginBottom: 8,
                        }}>
                          <RoomIcon style={{ fontSize: 14, color: '#22c55e' }} />
                          <Typography style={{ fontSize: '0.72rem', color: '#22c55e', fontWeight: 600 }}>
                            {Number(newLocation.latitude).toFixed(5)}, {Number(newLocation.longitude).toFixed(5)}
                          </Typography>
                          {newLocation.address && (
                            <Typography style={{ fontSize: '0.68rem', color: '#64748b', marginLeft: 'auto', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {newLocation.address}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Toggle coordenadas manuais */}
                      <Box style={{ marginBottom: 8 }}>
                        <Typography
                          onClick={() => setShowManualCoords(!showManualCoords)}
                          style={{
                            fontSize: '0.68rem', color: '#94a3b8', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4,
                          }}
                        >
                          {showManualCoords ? '▾' : '▸'} {t(lang, 'manualCoords')}
                        </Typography>
                      </Box>

                      {showManualCoords && (
                        <Box className={classes.formRow}>
                          <TextField size="small" variant="outlined" label="Latitude"
                            value={newLocation.latitude} type="number"
                            onChange={e => setNewLocation({ ...newLocation, latitude: e.target.value })}
                            InputProps={{ style: { fontSize: '0.82rem' } }}
                            InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                            style={{ flex: 1 }} />
                          <TextField size="small" variant="outlined" label="Longitude"
                            value={newLocation.longitude} type="number"
                            onChange={e => setNewLocation({ ...newLocation, longitude: e.target.value })}
                            InputProps={{ style: { fontSize: '0.82rem' } }}
                            InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                            style={{ flex: 1 }} />
                        </Box>
                      )}

                      {/* Notas */}
                      <Box className={classes.formRow}>
                        <TextField size="small" variant="outlined" label={t(lang, 'locationNotes')}
                          value={newLocation.notes} fullWidth multiline minRows={2}
                          onChange={e => setNewLocation({ ...newLocation, notes: e.target.value })}
                          InputProps={{ style: { fontSize: '0.82rem' } }}
                          InputLabelProps={{ style: { fontSize: '0.75rem' } }} />
                      </Box>

                      {/* Botões */}
                      <Box style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button size="small" className={classes.actionBtn}
                          onClick={() => { setShowAddForm(false); setShowManualCoords(false); setAddressInput(''); setAddressOptions([]); }}
                          style={{ color: '#64748b' }}>
                          {t(lang, 'cancel')}
                        </Button>
                        <Button size="small" variant="contained" className={classes.actionBtn}
                          onClick={handleAddLocation}
                          disabled={!newLocation.name || !newLocation.latitude || !newLocation.longitude}
                          style={{ background: '#3b82f6', color: '#fff' }}>
                          {t(lang, 'save')}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Locations list */}
                  {locationsLoading ? (
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : locations.length === 0 ? (
                    <Box className={classes.emptyState}>
                      <PlaceIcon style={{ fontSize: 44, color: '#cbd5e1' }} />
                      <Typography style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                        {t(lang, 'noLocations')}
                      </Typography>
                    </Box>
                  ) : (
                    locations.map(loc => {
                      const cfg = getTypeConfig(loc.type);
                      const TypeIcon = cfg.icon;
                      return (
                        <Box key={loc.id} className={classes.locationCard}>
                          <Box className={classes.locationIcon} style={{ background: `${cfg.color}12` }}>
                            <TypeIcon style={{ fontSize: 16, color: cfg.color }} />
                          </Box>
                          <Box className={classes.locationInfo}>
                            {editingLoc?.id === loc.id ? (
                              <input
                                autoFocus
                                value={editingLoc.name}
                                onChange={e => setEditingLoc({ ...editingLoc, name: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveLocationEdit(); if (e.key === 'Escape') setEditingLoc(null); }}
                                style={{
                                  border: 'none', borderBottom: '2px solid #3b82f6', background: 'transparent',
                                  fontSize: '0.82rem', color: '#0f172a', fontWeight: 600, outline: 'none',
                                  width: '100%', padding: '2px 0',
                                }}
                              />
                            ) : (
                              <Typography className={classes.locationName}>{loc.name}</Typography>
                            )}
                            <Typography className={classes.locationMeta}>
                              {getTypeLabel(lang, loc.type)}
                              {loc.address && ` · ${loc.address}`}
                              {` · ${formatDate(loc.createdAt)}`}
                            </Typography>
                            {loc.notes && (
                              <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontStyle: 'italic', marginTop: 2 }}>
                                {loc.notes}
                              </Typography>
                            )}
                          </Box>
                          <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                            {Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}
                          </Typography>
                          {editingLoc?.id === loc.id ? (
                            <Tooltip title={t(lang, 'save')}>
                              <IconButton size="small" onClick={handleSaveLocationEdit}
                                style={{ color: '#22c55e', padding: 4 }}>
                                <CheckIcon style={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t(lang, 'edit') || 'Editar'}>
                              <IconButton size="small" onClick={() => setEditingLoc({ id: loc.id, name: loc.name })}
                                style={{ color: '#94a3b8', padding: 4 }}>
                                <EditIcon style={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t(lang, 'delete')}>
                            <IconButton size="small" onClick={() => handleDeleteLocation(loc.id)}
                              style={{ color: '#94a3b8', padding: 4 }}>
                              <DeleteIcon style={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* ──── Route Detail Popup ──── */}
      {detailRoute && (
        <Box className={classes.detailOverlay} onClick={() => setDetailRoute(null)}>
          <Box className={classes.detailDialog} onClick={e => e.stopPropagation()}>
            <Box className={classes.detailDialogHeader}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Box style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: 'rgba(59,130,246,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <DirectionsIcon style={{ color: '#60a5fa', fontSize: 16 }} />
                </Box>
                <Box>
                  <Typography style={{ fontSize: '0.88rem', fontWeight: 600, color: '#ffffff' }}>
                    {detailRoute.name || t(lang, 'routeNoName')}
                  </Typography>
                  <Typography style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)' }}>
                    {detailRoute.origin?.name || '—'} → {detailRoute.destination?.name || '—'}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setDetailRoute(null)} style={{ color: 'rgba(255,255,255,0.5)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box className={classes.detailDialogBody}>
              {renderRouteDetail(detailRoute)}
            </Box>
          </Box>
        </Box>
      )}

      {/* ──── Delete Confirmation Dialog ──── */}
      {showDeleteConfirm && (
        <Box className={classes.confirmOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <Box className={classes.confirmDialog} onClick={e => e.stopPropagation()}>
            <Box className={classes.confirmHeader}>
              <Box className={classes.confirmIconWrap}>
                <WarningIcon style={{ fontSize: 22, color: '#ef4444' }} />
              </Box>
              <Box>
                <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a' }}>
                  {t(lang, 'delete')}
                </Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  {selectedRoutes.length} {t(lang, 'routesSelected')}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.confirmBody}>
              <Typography style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.5 }}>
                {t(lang, 'confirmDeleteRoutes')}
              </Typography>
              <Box style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {selectedRoutes.slice(0, 5).map(id => {
                  const r = savedRoutes.find(sr => sr.id === id);
                  return r ? (
                    <Box key={id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca' }}>
                      <DeleteIcon style={{ fontSize: 12, color: '#ef4444' }} />
                      <Typography style={{ fontSize: '0.7rem', color: '#991b1b', fontWeight: 500 }}>
                        {r.name || t(lang, 'routeNoName')}
                      </Typography>
                    </Box>
                  ) : null;
                })}
              </Box>
            </Box>
            <Box className={classes.confirmActions}>
              <Button className={classes.confirmCancelBtn} onClick={() => setShowDeleteConfirm(false)}>
                {t(lang, 'cancel')}
              </Button>
              <Button className={classes.confirmDeleteBtn} onClick={confirmDelete}
                startIcon={<DeleteIcon style={{ fontSize: 14 }} />}>
                {t(lang, 'delete')} ({selectedRoutes.length})
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
    <ConfirmDialog
      open={!!routeDeleteConfirm}
      onClose={() => setRouteDeleteConfirm(null)}
      onConfirm={() => {
        if (routeDeleteConfirm) {
          dispatch(deleteRouteFromAPI({ token, routeId: routeDeleteConfirm.dbId || routeDeleteConfirm.id }));
        }
      }}
      title={lang === 'pt' ? 'Eliminar Rota' : 'Delete Route'}
      message={lang === 'pt' ? `Tem a certeza que deseja eliminar "${routeDeleteConfirm?.name || 'rota'}"?` : `Are you sure you want to delete "${routeDeleteConfirm?.name || 'route'}"?`}
      severity="error"
      confirmLabel={lang === 'pt' ? 'Eliminar' : 'Delete'}
    />

    <DriverQuickEditDialog
      open={!!editDriver}
      onClose={() => setEditDriver(null)}
      driver={editDriver}
      token={token}
      lang={lang}
    />
    </>
  );
}

export { LOCATION_TYPES, getTypeLabel, getTypeConfig };
export default ManageDialog;
